import API from 'services/API';
import { OptionRenderers } from 'UI/utils/renderers';

import { NODE_CONFIGURATION, NODE_NAMES } from './pilIndustries.constants';

/**
 * @typedef {'industry'|'specialty'|'subspecialty'|'position'} NodeType
 */

/**
 * @typedef {Object} PilNode
 * @property {number} id
 * @property {NodeType} type
 * @property {string} [title]
 * @property {number} [industry_id]
 * @property {number} [specialty_id]
 */

/**
 * @typedef {Object} TreeNode
 * @property {string} id
 * @property {string} title
 * @property {NodeType} type
 */

/**
 * @param {string} nodeId
 * @returns {string[]}
 */
export const parseNodeId = nodeId => nodeId.split('-');

/**
 * @param {Object} tree
 * @param {string} nodeId
 * @returns {PilNode}
 */
export const findNodeInTree = (tree, nodeId) => {
  const visibleNodes = Object.keys(tree)
    .map(key => tree[key])
    .flat();

  const item = visibleNodes.find(({ id }) => id === nodeId);
  const [, id] = parseNodeId(nodeId);

  return {
    ...item,
    id
  };
};

/**
 * @param {PilNode[]} results
 * @returns {TreeNode[]}
 */
export const getTreeViewFormat = results =>
  results.map(({ id, title }) => ({
    id: `${NODE_CONFIGURATION[NODE_NAMES.Industry].Key}-${id}`,
    title,
    type: NODE_NAMES.Industry
  }));

/**
 * @param {NodeType} optionType
 * @param {Object} option
 * @returns {React.ReactNode}
 */
export const getRenderOption = (optionType, option) => {
  const {
    title,
    industry_title: industryTitle = '',
    specialty_title: specialtyTitle = ''
  } = option;

  const mapRenderOptions = {
    industry: title,
    specialty: OptionRenderers.specialty({ industry: industryTitle, title }),
    subspecialty: OptionRenderers.subspecialty({
      industry: industryTitle,
      specialty: specialtyTitle,
      title
    }),
    position: OptionRenderers.position({
      industry: industryTitle,
      specialty: specialtyTitle,
      position: title
    })
  };

  return mapRenderOptions[optionType];
};

/**
 * @param {NodeType} optionType
 * @param {Object} option
 * @returns {string}
 */
export const getOptionLabel = (optionType, option) => {
  const {
    title,
    industry_title: industryTitle = '',
    specialty_title: specialtyTitle = ''
  } = option;

  const mapOptionLabels = {
    industry: title,
    specialty: `${industryTitle} › ${title}`,
    subspecialty: `${industryTitle} › ${specialtyTitle} › ${title}`,
    position: `${industryTitle} › ${specialtyTitle}`
  };

  return mapOptionLabels[optionType];
};

const getIndustryIdBySpecialtyId = (industries, specialtyId) => {
  const industry = industries.find(ind =>
    ind.specialties.some(specialty => specialty.id === specialtyId)
  );
  return industry ? industry.id : null;
};

/**
 * @param {PilNode} selection
 * @param {Object[]} results
 * @returns {(number|null)}
 */
export const getSelectionIndustryId = (
  { id, industry_id: industryId, specialty_id: specialtyId, type },
  results
) => {
  if (type === 'position') {
    const hasIndustryId = !!industryId;
    return hasIndustryId ? industryId : getIndustryIdBySpecialtyId(results, specialtyId);
  }

  return type === 'industry' ? id : industryId;
};

/**
 * @param {PilNode} selection
 * @returns {(number|null)}
 */
export const getSelectionSpecialtyId = ({ id, specialty_id: specialtyId, type }) => {
  if (type === 'industry') return null;
  if (type === 'position') return specialtyId;

  return type === 'specialty' ? id : specialtyId;
};

/**
 * @param {PilNode} selection
 * @param {number} [optionalIndustryId]
 * @returns {string[]}
 */
export const getExpandedIds = (
  { id, industry_id: industryId, specialty_id: specialtyId, type },
  optionalIndustryId
) => {
  const mapIds = {
    industry: [`industry-${id}`],
    specialty: [`industry-${industryId}`, `specialty-${id}`],
    subspecialty: [`industry-${industryId}`, `specialty-${specialtyId}`],
    position: [`industry-${industryId ?? optionalIndustryId}`, `specialty-${specialtyId}`]
  };

  return mapIds[type];
};

/**
 * @param {Object} params
 * @param {number} params.id
 * @param {NodeType} params.type
 * @returns {Promise<(TreeNode[]|null)>}
 */
export const getPilOptions = async ({ id: selectedId, type }) => {
  if (!selectedId) return null;

  try {
    const nodeConfig = NODE_CONFIGURATION[type];
    if (!nodeConfig || !nodeConfig.ChildrenEndpoint) return null;

    const endpoint = nodeConfig.ChildrenEndpoint.replace(':id', selectedId);
    const response = await API.get(endpoint);

    return response.data.map(({ id, title }) => ({
      id: `${nodeConfig.ChildrenKey}-${id}`,
      title,
      type: nodeConfig.ChildrenKey
    }));
  } catch (error) {
    return null;
  }
};
